import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Heading,
    Text,
    Container,
    Flex,
    Box,
    Image,
    Button,
    Spinner,
    Center
} from "@chakra-ui/react";

export const CourseSuggestion = () => {
    const { suggestionID, course_title } = useParams(); // suggestionID links specific campaign to specific individuals, course_title is optional for redirect
    const navigate = useNavigate();
    const [ participantInfo, setParticipantInfo ] = useState({
        participantEmail: "",
        participantName: "", // Need to extract latest name use
    });
    
    const [ completedCourses, setCompletedCourses ] = useState([]);
    // Array of objects: [{courseCategory: [...], courseCode: "...", ...}, ...]
        // courseCategory: [], // Supabase JSON data type for course_category column [{categoryName, categoryIndex}, ...], one course can be in multiple categories 
        // courseCode: "",
        // courseName: "",
        // courseDesc: "",
        // courseImage: "",
        // courseIcon: [], // Supabase JSON data type for course_icon column [{iconURL, iconName}, ...], each course can have multiple icons
        // courseLink: "",
        // endDate: "", // Used for sorting by latest end date first
        // formatEndDate: "",
    const [ completedCategory, setCompletedCategory ] = useState([]);
    // Might not use in UI but good to have as intermediate step / future use
    // Array of objects: [{courseCategory: "...", countCompletedCourses: ...}, ...]
        // courseCategory: "", // Obtain completed course category
        // countCompletedCourses: 0 // Count number of completed courses in each course category, used for sorting
    const [ suggestedCourses, setSuggestedCourses ] = useState([]);
    // Array of objects: [{courseCategory: [...], courseCode: "...", ...}, ...]
        // courseCategory: [], // Supabase JSON data type for course_category column [{categoryName, categoryIndex}, ...], one course can be in multiple categories 
        // courseCode: "",
        // courseName: "",
        // courseDesc: "",
        // courseImage: "",
        // courseIcon: [], // Supabase JSON data type for course_icon column [{iconURL, iconName}, ...], each course can have multiple icons
        // courseLink: "",
    const [ suggestedCategory, setSuggestedCategory ] = useState([]);
    // suggestedCategory avoids course category that is totally completed by participant
    // Array of objects: [{courseCategory: "...", relevance: ...}, ...]
        // courseCategory: "", // Obtain suggested course category
        // relevance: 0 // Relevance scoring for suggested course category sorting (most relevant course category = highest countCompletedCourses)
    const [ isCheckingRedirect, setIsCheckingRedirect ] = useState(false); // Loading state when checking for course_title redirect


    // HELPER FUNCTIONS

    // Helper function to slugify course names (matches the exact rules provided)
    const slugify = (text) => {
        if (!text) return "";
        
        // Step 1: Convert to lowercase
        let slug = text.toLowerCase();
        
        // Step 2: Replace non-breaking spaces (\u00A0), en-space, em-space, thin-space with regular space
        slug = slug.replace(/[\u00A0\u2002\u2003\u2009]/g, " ");
        
        // Step 3: Remove zero-width spaces (\u200B)
        slug = slug.replace(/\u200B/g, "");
        
        // Step 4: Replace newlines, carriage returns, tabs with space
        slug = slug.replace(/[\n\r\t]/g, " ");
        
        // Step 5: Remove all special characters except alphanumeric, spaces, hyphens
        slug = slug.replace(/[^a-z0-9\s-]/g, "");
        
        // Step 6: Replace spaces with hyphens
        slug = slug.replace(/\s+/g, "-");
        
        // Step 7: Collapse multiple hyphens into one
        slug = slug.replace(/-+/g, "-");
        
        // Step 8: Strip leading/trailing hyphens
        slug = slug.replace(/^-+|-+$/g, "");
        
        return slug;
    };

    // Helper function to update interaction JSONB column in universal_campaign_link table
    const updateInteraction = async (interactionType, data) => {
        try {
            // First, get the current row to retrieve existing interaction data, row ID, and participant email
            const { data: currentData, error: fetchError } = await supabase
                .from("universal_campaign_link")
                .select("id, participant_email, interaction")
                .like("campaign_link", `%${suggestionID}`)
                .single();

            if (fetchError) {
                console.error(`Error fetching interaction data for suggestionID: ${suggestionID}`, fetchError);
                return;
            }

            if (!currentData || !currentData.id || !currentData.participant_email) {
                console.error(`No row found or missing data for suggestionID: ${suggestionID}`);
                return;
            }

            // Initialize interaction object if it doesn't exist
            const currentInteraction = currentData?.interaction || {};
            
            // Initialize arrays if they don't exist
            if (!currentInteraction.pageLoad) {
                currentInteraction.pageLoad = [];
            }
            if (!currentInteraction.viewCourseButton) {
                currentInteraction.viewCourseButton = [];
            }

            // Add new interaction data
            if (interactionType === "pageLoad") {
                currentInteraction.pageLoad.push({
                    timestamp: new Date().toISOString(),
                    suggestionID: suggestionID,
                    redirectPath: data?.redirectPath || null
                });
            } else if (interactionType === "viewCourseButton") {
                currentInteraction.viewCourseButton.push({
                    timestamp: new Date().toISOString(),
                    courseCode: data.courseCode,
                    courseName: data.courseName,
                    courseLink: data.courseLink,
                    courseType: data.courseType // "completed" or "suggested"
                });
            }

            // Update the interaction column using both row ID and participant email for extra safety
            const { error: updateError } = await supabase
                .from("universal_campaign_link")
                .update({ interaction: currentInteraction })
                .eq("id", currentData.id)
                .eq("participant_email", currentData.participant_email);

            if (updateError) {
                console.error(`Error updating interaction data for suggestionID: ${suggestionID}`, updateError);
            }
        } catch (error) {
            console.error(`Error in updateInteraction for suggestionID: ${suggestionID}`, error);
        }
    };

    // Helper function to convert completed course end date to days, months, years ago from current date (display purpose)
    const formatTimeDifference = (endDate) => {
        if (!endDate) return "";
        const now = new Date();
        const end = new Date(endDate); // As endDate is a string
        const diffTime = now - end; // Subtraction of Date objects results in difference in milliseconds, now - end > 0
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Convert from ms to days, value rounded down 
        
        let timeString = "";
        if (diffDays === 0 || diffDays === 1){
            timeString = "recently"
        } else if (diffDays < 7){
            timeString = `${diffDays} days ago`;
        } else if (diffDays < 30){
            const diffWeeks = Math.floor(diffDays/ 7); // Convert days to weeks, round down
            timeString = `${diffWeeks === 1 ? "a week ago" : `${diffWeeks} weeks ago`}`;
        } else if (diffDays < 365){
            const diffMonths = Math.floor(diffDays/ 30); // Convert days to months, round down
            timeString = `${diffMonths === 1 ? "a month ago" : `${diffMonths} months ago`}`;
        } else {
            const diffYears = Math.floor(diffDays / 365); // Convert days to years, round down
            timeString = `${diffYears === 1 ? "just last year" : `around ${diffYears} years ago`}`;
        };
        return timeString;
    };


    // MAIN USEEFFECT WORKFLOW

    // Handle redirect when course_title is present in URL
    useEffect(() => {
        const handleCourseRedirect = async () => {
            if (!course_title) {
                setIsCheckingRedirect(false); // No course_title, proceed with normal page load
                return;
            }

            // Set loading state to show spinner instead of page content
            setIsCheckingRedirect(true);

            try {
                // Fetch all courses from course_type table
                const { data: coursesData, error: coursesError } = await supabase
                    .from("course_type")
                    .select("name, course_link");

                if (coursesError) {
                    console.error(`Error fetching courses for redirect: ${coursesError}`);
                    setIsCheckingRedirect(false); // Let normal page load if error
                    return;
                }

                if (!coursesData || coursesData.length === 0) {
                    console.error("No courses found in course_type table");
                    setIsCheckingRedirect(false); // Let normal page load if no courses
                    return;
                }

                // Slugify each course name and find match
                const matchedCourse = coursesData.find(course => {
                    const slugifiedName = slugify(course.name);
                    return slugifiedName === course_title;
                });

                if (matchedCourse && matchedCourse.course_link) {
                    // Track the redirect before redirecting
                    await updateInteraction("pageLoad", { redirectPath: matchedCourse.course_link });
                    
                    // Redirect to the course_link (do not load campaign_link page)
                    window.location.replace(matchedCourse.course_link);
                } else {
                    console.error(`No course found matching slugified name: ${course_title}`);
                    // If no match found, redirect to base course-suggestion page without course_title
                    setIsCheckingRedirect(false);
                    navigate(`/course-suggestion/${suggestionID}`, { replace: true });
                }
            } catch (error) {
                console.error(`Error in handleCourseRedirect: ${error}`);
                // On error, redirect to base page
                setIsCheckingRedirect(false);
                navigate(`/course-suggestion/${suggestionID}`, { replace: true });
            }
        };

        handleCourseRedirect();
    }, [course_title, suggestionID, navigate]); // Run when course_title or suggestionID changes

    useEffect(() => {
        const fetchData = async () => { // Fetch data from Supabase, takes time, therefore async function

            const { data:uclData, error:uclError } = await supabase // ucl: Universal Campaign Link Table
                .from("universal_campaign_link") 
                .select("participant_email")
                .like("campaign_link", `%${suggestionID}`) // suggestionID is at the end of the string in campaign_link column
                .single(); // One result of participant email is expected, suggestionID is unique to type of campaigns within each participant as well
            
            const { data:ctData, error:ctError } = await supabase // ct: Course Type Table
                .from("course_type")
                .select("*");
            
            if (uclError || ctError){
                if (uclError) {
                    console.error(`Error fetching data from universal_campaign_link table for participant suggestionID: ${suggestionID}, error: `, uclError)
                } else {
                    console.error(`Error fetching data from course_type table for participant suggestionID: ${suggestionID}, error: `,ctError)
                };
            } else if (uclData && uclData.participant_email && ctData && ctData.length > 0){ // Both data is truthy: not undefined, null or empty object/arrays (uclData is an object, cannot use .length)

                const { data:frData, error:frError } = await supabase // fr: Full Registration Details View, requires valid participant email from uclData
                    .from("full_registration_details")
                    .select("participant_name, code, end_date") // Note that participant_name might be > 1 for the same person if registered for mutliple courses
                    .eq("participant_email", uclData.participant_email);

                    if (frError) {
                        console.error(`Error fetching data from full_registration_details view for participant suggestionID: ${suggestionID}, error: `,frError)
                    } else if (frData && frData.length > 0) { // Data is truthy: not undefined, null or empty arrays
                        
                        // Setting up participantInfo: email from uclData and name from frData
                        const temp_participantEmail = uclData.participant_email;
                        const sortFrData = [...frData].sort((a,b) => new Date(b.end_date) - new Date(a.end_date)); // Copy frData and sort it based on latest end date (participant name, course code as well) being indexed first
                        
                        if (sortFrData.length > 0) {
                            setParticipantInfo({
                                participantEmail: temp_participantEmail,
                                participantName: sortFrData[0].participant_name // Use latest name from sorted frData
                            });
                        };

                        // Setting up completedCourses from sortFrData (participant completed course code, endDate) and ctData (all course details)
                        // Link sortFrData with ctData through matching course code
                        const temp_completedCourses = sortFrData.map(course => {
                            const matchingCourse = ctData.find(ct => ct.code === course.code); // Code must be found in ctData
                            return {
                                courseCategory: matchingCourse.course_category, // Populate with [{categoryName, categoryIndex}, ...]
                                courseCode: course.code,
                                courseName: matchingCourse.name,
                                courseDesc: matchingCourse.short_desc,
                                courseImage: matchingCourse.course_image,
                                courseIcon: matchingCourse.course_icon, // Populate with [{iconURL, iconName}, ...]
                                courseLink: matchingCourse.course_link,
                                endDate: course.end_date,
                                formatEndDate: formatTimeDifference(course.end_date), // Helper function
                            };
                        }).sort((a,b) => new Date(b.endDate) - new Date(a.endDate)); // Sort by latest end date first for completed courses

                        setCompletedCourses(temp_completedCourses);

                        // Setting up completedCategory from temp_completedCourses
                        const temp_completedCategory = temp_completedCourses.flatMap(
                            course => course.courseCategory?.map(cat => cat?.categoryName) ?? [] // Can contain repeated category names
                        );
                        const temp_completedCategoryCount = [...new Set(temp_completedCategory)].map(unique => {
                            const count = temp_completedCategory.filter(cat => cat === unique).length;
                            return {
                                courseCategory: unique,
                                countCompletedCourses: count // Count number of completed courses in each course category (include overlap count - course that appear in more than one category)
                            };
                        }).sort((a,b) => b.countCompletedCourses - a.countCompletedCourses); // Sort by highest countCompletedCourses first

                        setCompletedCategory(temp_completedCategoryCount);

                        // Setting up suggestedCourses from temp_completedCourses and ctData (all course details)
                        const temp_suggestedCourses = ctData
                            .filter(course => course.course_category && course.course_category.length > 0) // Filter out courses with no course category
                            .filter(course => !temp_completedCourses.some(completed => completed.courseCode === course.code)) // Filter out courses which are completed via course code
                            .map(course => {
                                return {
                                    courseCategory: course.course_category, // Populate with [{categoryName, categoryIndex}, ...]
                                    courseCode: course.code,
                                    courseName: course.name,
                                    courseDesc: course.short_desc,
                                    courseImage: course.course_image,
                                    courseIcon: course.course_icon, // Populate with [{iconURL, iconName}, ...]
                                    courseLink: course.course_link
                                };
                            });
                        
                        setSuggestedCourses(temp_suggestedCourses);

                        // Setting up suggestedCategory from temp_suggestedCourses and temp_completedCategoryCount
                        const temp_suggestedCategory = [...new Set(temp_suggestedCourses.flatMap( // Obtain unique course categories from suggested courses
                            course => course.courseCategory?.map(cat => cat?.categoryName) ?? []
                        ))];
                        const temp_suggestedCatRelevance = temp_suggestedCategory.map(cat => {
                            const temp_relevance = temp_completedCategoryCount.find(completed => completed.courseCategory === cat)?.countCompletedCourses ?? 0;
                            return {
                                courseCategory: cat,
                                relevance: temp_relevance // Completed course count in category = relevance
                            };
                        }).sort((a,b) => b.relevance - a.relevance); // Sort by highest relevance first for suggested course categories

                        setSuggestedCategory(temp_suggestedCatRelevance);


                    } else { // Error handling of full_registration_details view query
                        console.error(`No data found from full_registration_details view for participant suggestionID: ${suggestionID} and participant email: ${uclData.participant_email}`)
                    };

            } else {
                // Error handling of universal_campaign_link table and course_type table queries
                if (!uclData || !uclData.participant_email) {
                    if (!ctData || ctData.length === 0) {
                        console.error(`No data found from course_type table and universal_campaign_link table for participant suggestionID: ${suggestionID}`)
                    } else {
                        console.error(`No data found from universal_campaign_link table for participant suggestionID: ${suggestionID}`)
                    };
                } else {
                    console.error(`No data found from course_type table for participant suggestionID: ${suggestionID}`)
                };
            };
        };

        fetchData();

    }, [suggestionID]); // Update when suggestionID changes

    // Track initial page load (only when not redirecting via course_title)
    useEffect(() => {
        // Skip tracking if course_title is present (redirect will handle tracking)
        if (course_title) {
            return;
        }
        
        // Track that someone reached the page (this happens immediately on mount, before any data loads)
        // This captures anyone who visits the URL, even if they redirect immediately
        updateInteraction("pageLoad", { redirectPath: null }); // null means they arrived but no redirect happened yet
    }, [suggestionID, course_title]); // Track on mount and when suggestionID changes


    // Show loading spinner when checking for redirect (course_title present)
    if (isCheckingRedirect) {
        return (
            <Container
                minH="100vh"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="#F4EEEA"
            >
                <Center>
                    <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="#D4785C"
                        size="xl"
                    />
                </Center>
            </Container>
        );
    }

    return(
        <>
            {/* Hero Section*/}
            <Container
                // Base as mobile screen, md as tablet screen, lg as desktop screen
                maxW={{ base: "100%", md: "container.md", lg: "container.lg" }}
                py={{ base: 4, md: 6, lg: 8 }}
                px={{ base: 4, md: 0 }}
            >
                <Box
                    bg="white"
                    p={{ base: 4, md: 6, lg: 8 }}
                    borderRadius={{ base: "md", md: "lg" }}
                    borderWidth="thin"
                    borderStyle="solid"
                    borderColor="#E8DFD8" // Darker than Sozcode Beige Color #F4EEEA
                >
                    <Heading
                        as="h1"
                        size={{ base: "sm", md: "lg", lg: "xl" }}
                        fontWeight="bold"
                        color="#0E1735" // Sozcode Navy Blue Color
                        mb={{ base: 2, md: 4 }}
                    >
                        Hello, {participantInfo.participantName}!
                    </Heading>
                    <Text
                        as="p"
                        fontSize={{ base: "sm", md: "md" }}
                        fontWeight="semibold"
                        color="#D4785C" // Sozcode Coral Color
                    >
                        Discover the amazing courses we have specially curated for you!
                    </Text>
                </Box>
            </Container>

            {/* Completed Courses Section */}
            <Container
                maxW={{ base: "100%", md: "container.md", lg: "container.lg" }}
                py={{ base: 4, md: 6, lg: 8 }}
                px={{ base: 4, md: 0 }}
            >
                <Box
                    bg="white"
                    p={{ base: 4, md: 6 }}
                    borderRadius={{ base: "md", md: "lg" }}
                    borderWidth="thin"
                    borderStyle="solid"
                    borderColor="#E8DFD8" // Darker than Sozcode Beige Color #F4EEEA
                >
                    <Text
                        as="p"
                        fontSize={{ base: "sm", md: "md" }}
                        fontWeight="semibold"
                        letterSpacing={{ base: "wide", lg: "wider" }}
                        textTransform="uppercase"
                        color="#D4785C" // Sozcode Coral Color
                        mb={{ base: 2, md: 4 }}
                    >
                        Congratulations! You've mastered these courses
                    </Text>
                    <Flex
                        direction="row"
                        wrap="nowrap"
                        justify="left"
                        overflow="auto"
                        gap="1"
                        sx={{
                            '&::-webkit-scrollbar': { // Hide scrollbar for Chrome, Safari and Opera
                                display: "none",
                            },
                            scrollbarWidth: "none", // Hide scrollbar for Firefox
                            msOverflowStyle: "none" // Hide scrollbar for IE/Edge
                        }}
                    >
                        {completedCourses
                            .map(course => (
                                <Box
                                    // Parent box for completed course card
                                    key={course.courseCode}
                                    bg="#0E1735" // Sozcode Navy Blue Color
                                    // Width of parent box is such that: appears 1.5 cards on mobile, 2.5 cards on tablet, 3.5 cards on desktop
                                    width={{ base: "calc((100% - 0.75rem) / 1.5)", md: "calc((100% - 2rem) / 2.5)", lg: "calc((100% - 3rem) / 3.5)"}}
                                    height={{ base: "400px", lg: "440px" }}
                                    flex="none" // Avoid parent box from growing or shrinking
                                    px={{ base: 2, md: 4}}
                                    position="relative" // For absolute positioning of child elements
                                    overflow="hidden"
                                    borderRadius={{ base: "md", md: "lg" }}
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="flex-start" // Stack items from top
                                >
                                    
                                    {/* Box for course image: 40% of parent height */}
                                    <Box
                                        width={{ base: "calc(100% + 2rem)", md: "calc(100% + 3rem)" }} // For image to extend beyong padding of parent box
                                        height="40%"
                                        marginLeft={{ base: "-1rem", md: "-1.5rem" }}
                                        flex="none" // Avoid box from growing or shrinking
                                        overflow="hidden"
                                    >
                                        <Image
                                            src={course.courseImage}
                                            alt={course.courseName}
                                            width="100%"
                                            height="100%" // Might need to change
                                            objectFit="cover" // Crop image to fit, preserve aspect ratio
                                            objectPosition="center"
                                        />
                                    </Box>

                                    {/* Box for course name: 15% of parent height*/}
                                    <Box
                                        height="15%"
                                        flex="none"
                                        pt="2"
                                    >
                                        <Text
                                            fontWeight="semibold"
                                            color="white"
                                            textShadow="2px 2px 4px rgba(0,0,0,0.7)" // Improve contrast between text and background
                                            fontSize={{ base: "sm", md: "md"}}
                                        >
                                            {course.courseName}
                                        </Text>
                                    </Box>

                                    {/* Box for course icon: 10% of parent height */}
                                    <Box
                                        height="10%"
                                        flex="none"
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="flex-start" // Icons insert from the left
                                        gap="2px"
                                    >
                                        {course.courseIcon && course.courseIcon.length > 0 && (
                                            course.courseIcon.map((icon, index) => ( // icon contains iconURL and iconName
                                            <Box
                                                key={index}
                                                flex="none"
                                                display="flex"
                                                borderRadius={{ base: "sm", md: "md" }}
                                                borderWidth="thin"
                                                borderStyle="solid"
                                                borderColor="gay.300"
                                                flexDirection="row"
                                                alignItems="center"
                                                padding="2px"
                                                gap="2px"
                                            >
                                                <Image
                                                    alt={icon.iconName}
                                                    src={icon.iconURL}
                                                    sx={{filter: "invert(50%)"}} // Invert colors of icon
                                                    width={{ base: "14px", md: "16px" }}
                                                    height={{ base: "14px", md: "16px" }}
                                                    objectFit="contain"
                                                />
                                                <Text
                                                    fontSize="8px"
                                                    fontWeight="medium"
                                                    letterSpacing="wide"
                                                    textTransform="uppercase"
                                                    color="gray.300"
                                                >
                                                    {icon.iconName}
                                                </Text>
                                            </Box>
                                            ))
                                        )}
                                    </Box>

                                    {/* Box for format enddate: 15% of parent height */}
                                    <Box
                                        height="15%"
                                        flex="none"
                                        overflow="hidden"
                                    >
                                        <Text
                                            color="white"
                                            fontSize="sm"
                                        >
                                            You have taken this course {" "}
                                            <Text
                                                as="span"
                                                fontWeight="semibold"
                                                color="#7dd3FC" // Sky Blue Color
                                            >
                                                {course.formatEndDate}!
                                            </Text>
                                        </Text>
                                    </Box>
                                    
                                    {/* Box for view course button: 15% of parent height */}
                                    <Box
                                        height="15%"
                                        width="100%"
                                        flex="none"
                                        marginTop="auto" // Push button to the bottom
                                        display="flex"
                                        justifyContent="flex-end"
                                    >
                                        <Button
                                            as="a"
                                            href={course.courseLink}
                                            target="_blank" // Open link in new tab
                                            rel="noopener noreferrer" // Prevent security risks
                                            bg="transparent"
                                            color="#D4785C" // Sozcode Coral Color
                                            cursor="pointer" // Change cursor to pointer when hovering over button
                                            _hover={{color: "white"}}
                                            onClick={() => updateInteraction("viewCourseButton", {
                                                courseCode: course.courseCode,
                                                courseName: course.courseName,
                                                courseLink: course.courseLink,
                                                courseType: "completed"
                                            })}
                                        >
                                            View Course
                                        </Button>
                                    </Box>
                                </Box>
                            ))
                        }

                    </Flex>   
                </Box>
            </Container>

            {/* Suggested Courses Section - One Container per Course Category */}
            {suggestedCategory.map(category => (
                <Container
                    key={category.courseCategory}
                    maxW={{ base: "100%", md: "container.md", lg: "container.lg" }}
                    py={{ base: 4, md: 6, lg: 8 }}
                    px={{ base: 4, md: 0 }}
                >
                    <Box
                        bg="white"
                        p={{ base: 4, md: 6 }}
                        borderRadius={{ base: "md", md: "lg" }}
                        borderWidth="thin"
                        borderStyle="solid"
                        borderColor="#E8DFD8" // Darker than Sozcode Beige Color #F4EEEA
                    >
                        <Text
                            as="p"
                            fontSize={{ base: "sm", md: "md" }}
                            fontWeight="semibold"
                            letterSpacing={{ base: "wide", lg: "wider" }}
                            textTransform="uppercase"
                            color="#D4785C" // Sozcode Coral Color
                            mb={{ base: 2, md: 4 }}
                        >
                            Because you are interested in {" "}
                            <Text
                                as="span"
                                color="#0E1735" // Sozcode Navy Blue Color
                            >
                                {category.courseCategory}
                            </Text>
                        </Text>
                        <Flex
                            direction="row"
                            wrap="nowrap"
                            justify="left"
                            overflow="auto"
                            gap="1"
                            sx={{
                                '&::-webkit-scrollbar': { // Hide scrollbar for Chrome, Safari and Opera
                                    display: "none",
                                },
                                scrollbarWidth: "none", // Hide scrollbar for Firefox
                                msOverflowStyle: "none" // Hide scrollbar for IE/Edge
                            }}
                        >
                            {suggestedCourses
                            .filter(course => course.courseCategory.some(cat => cat.categoryName === category.courseCategory))
                            .sort((a,b) => {
                                // Sort the courses within the same category based on their categoryIndex (smaller index comes first)
                                const catA = a.courseCategory.find(cat => cat.categoryName === category.courseCategory);
                                const catB = b.courseCategory.find(cat => cat.categoryName === category.courseCategory);
                                return (catA?.categoryIndex || 0) - (catB?.categoryIndex || 0);
                            })
                            .map(course => (
                                <Box
                                    // Parent box for suggested course card
                                    key={course.courseCode}
                                    bg="#0E1735" // Sozcode Navy Blue Color
                                    // Width of parent box is such that: appears 1.5 cards on mobile, 2.5 cards on tablet, 3.5 cards on desktop
                                    width={{ base: "calc((100% - 0.75rem) / 1.5)", md: "calc((100% - 2rem) / 2.5)", lg: "calc((100% - 3rem) / 3.5)" }}
                                    height={{ base: "400px", lg: "440px" }}
                                    flex="none" // Avoid parent box from growing or shrinking
                                    px={{ base: 2, md: 4}}
                                    position="relative" // For absolute positioning of child elements
                                    overflow="hidden"
                                    borderRadius={{ base: "md", md: "lg" }}
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="flex-start" // Stack items from top
                                >
                                    
                                    {/* Box for course image: 40% of parent height */}
                                    <Box
                                        width={{ base: "calc(100% + 2rem)", md: "calc(100% + 3rem)" }} // For image to extend beyong padding of parent box
                                        height="40%"
                                        marginLeft={{ base: "-1rem", md: "-1.5rem" }}
                                        flex="none" // Avoid box from growing or shrinking
                                        overflow="hidden"
                                    >
                                        <Image
                                            src={course.courseImage}
                                            alt={course.courseName}
                                            width="100%"
                                            height="100%" // Might need to change
                                            objectFit="cover" // Crop image to fit, preserve aspect ratio
                                            objectPosition="center"
                                        />
                                    </Box>

                                    {/* Box for course name: 15% of parent height*/}
                                    <Box
                                        height="15%"
                                        flex="none"
                                        pt="2"
                                    >
                                        <Text
                                            fontWeight="semibold"
                                            color="white"
                                            textShadow="2px 2px 4px rgba(0,0,0,0.7)" // Improve contrast between text and background
                                            fontSize={{ base: "sm", md: "md"}}
                                        >
                                            {course.courseName}
                                        </Text>
                                    </Box>

                                    {/* Box for course icon: 10% of parent height */}
                                    <Box
                                        height="10%"
                                        flex="none"
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="flex-start" // Icons insert from the left
                                        gap="4px"
                                    >
                                        {course.courseIcon && course.courseIcon.length > 0 && (
                                            course.courseIcon.map((icon, index) => ( // icon contains iconURL and iconName
                                            <Box
                                                key={index}
                                                flex="none"
                                                display="flex"
                                                borderRadius={{ base: "sm", md: "md" }}
                                                borderWidth="thin"
                                                borderStyle="solid"
                                                borderColor="gray.300"
                                                flexDirection="row"
                                                alignItems="center"
                                                padding="2px"
                                                gap="2px"
                                            >
                                                <Image
                                                    alt={icon.iconName}
                                                    src={icon.iconURL}
                                                    sx={{filter: "invert(50%)"}} // Invert colors of icon
                                                    width={{ base: "14px", md: "16px" }}
                                                    height={{ base: "14px", md: "16px" }}
                                                    objectFit="contain"
                                                />
                                                <Text
                                                    fontSize="8px"
                                                    fontWeight="medium"
                                                    letterSpacing="wide"
                                                    textTransform="uppercase"
                                                    color="gray.300"
                                                >
                                                    {icon.iconName}
                                                </Text>
                                            </Box>
                                            ))
                                        )}
                                    </Box>

                                    {/* Box for course desciption: 15% of parent height */}
                                    <Box
                                        height="15%"
                                        flex="none"
                                        overflow="hidden"
                                    >
                                        <Text
                                            color="gray.300"
                                            fontSize="sm"
                                            overflow="hidden"
                                            textOverflow="ellipsis" // Show ellipsis for overflow text
                                            sx={{
                                                display:"-webkit-box",
                                                WebkitLineClamp: 3, // Max 3 lines for course_desc
                                                WebkitBoxOrient: "vertical",
                                            }}
                                        >
                                            {course.courseDesc}
                                        </Text>
                                    </Box>
                                    
                                    {/* Box for view course button: 15% of parent height */}
                                    <Box
                                        height="15%"
                                        width="100%"
                                        flex="none"
                                        marginTop="auto" // Push button to the bottom of the box
                                        display="flex"
                                        justifyContent="flex-end"
                                    >
                                        <Button
                                            as="a"
                                            href={course.courseLink}
                                            target="_blank" // Open link in new tab
                                            rel="noopener noreferrer" // Prevent security risks
                                            bg="transparent"
                                            color="#D4785C" // Sozcode Coral Color
                                            cursor="pointer" // Change cursor to pointer when hovering over button
                                            _hover={{color: "white"}}
                                            onClick={() => updateInteraction("viewCourseButton", {
                                                courseCode: course.courseCode,
                                                courseName: course.courseName,
                                                courseLink: course.courseLink,
                                                courseType: "suggested"
                                            })}
                                        >
                                            View Course
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Flex>   
                    </Box>
                </Container>
            ))}
        </>
    );
}